import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Play, Loader2 } from "lucide-react";

import {
  getImageTranscodeStats,
  runImageTranscodeBatch,
} from "@/lib/image-transcode.functions";

export const Route = createFileRoute("/_authenticated/admin/image-optimization")({
  component: ImageOptimizationPage,
});

function fmtMB(bytes: number) {
  return (bytes / (1024 * 1024)).toFixed(2) + " MB";
}

function ImageOptimizationPage() {
  const statsFn = useServerFn(getImageTranscodeStats);
  const runFn = useServerFn(runImageTranscodeBatch);
  const [running, setRunning] = useState(false);
  const [log, setLog] = useState<string[]>([]);

  const stats = useQuery({
    queryKey: ["image-transcode-stats"],
    queryFn: () => statsFn(),
    refetchInterval: running ? 3000 : 30000,
  });

  const s = stats.data;
  const pct = s && s.totalCandidates > 0 ? Math.round((s.processed / s.totalCandidates) * 100) : 0;

  async function runBatch(limit: number, loop: boolean) {
    setRunning(true);
    setLog([]);
    try {
      let total = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const res = await runFn({ data: { limit } });
        total += res.processed;
        setLog((prev) => [
          ...prev,
          ...res.results.map(
            (r) =>
              (r.ok ? "✓" : "✗") +
              " " +
              r.url.split("/").pop() +
              (r.ok
                ? ` — saved ${fmtMB(r.saved ?? 0)}`
                : ` — ${r.error?.slice(0, 80) ?? "error"}`),
          ),
        ]);
        stats.refetch();
        if (!loop || res.processed === 0) break;
        // brief yield between batches
        await new Promise((r) => setTimeout(r, 300));
      }
      toast.success(`Processed ${total} image${total === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Batch failed");
    } finally {
      setRunning(false);
      stats.refetch();
    }
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 space-y-6">
      <Link
        to="/admin"
        search={{ page: "settings" } as never}
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to admin
      </Link>

      <div>
        <h1 className="text-2xl font-bold text-foreground">Image Optimization</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Backfill AVIF + WebP variants for every existing photo in the{" "}
          <code className="rounded bg-muted px-1">content-images</code> bucket. Originals
          are never modified — variants are stored alongside as{" "}
          <code className="rounded bg-muted px-1">file.jpg.avif</code> and{" "}
          <code className="rounded bg-muted px-1">file.jpg.webp</code>.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total" value={s ? s.totalCandidates.toLocaleString() : "…"} />
        <StatCard label="Processed" value={s ? s.processed.toLocaleString() : "…"} tone="success" />
        <StatCard label="Pending" value={s ? s.pending.toLocaleString() : "…"} />
        <StatCard label="Errored" value={s ? s.errored.toLocaleString() : "…"} tone={s?.errored ? "warn" : undefined} />
      </div>

      {s && s.totalCandidates > 0 && (
        <div className="space-y-2">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{pct}% complete</span>
            <span>
              Saved {fmtMB(s.savedBytes)} · {fmtMB(s.originalBytes)} → {fmtMB(s.avifBytes)}{" "}
              (AVIF)
            </span>
          </div>
          <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => runBatch(8, false)}
          disabled={running || !s || s.pending === 0}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          Run one batch (8)
        </button>
        <button
          onClick={() => runBatch(8, true)}
          disabled={running || !s || s.pending === 0}
          className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium disabled:opacity-50"
        >
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          Process all pending (loop)
        </button>
      </div>

      {log.length > 0 && (
        <div className="rounded-lg border border-border bg-muted/30 p-3">
          <div className="mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Recent activity
          </div>
          <ul className="max-h-80 overflow-auto space-y-0.5 font-mono text-xs">
            {log.slice(-100).map((line, i) => (
              <li key={i} className={line.startsWith("✗") ? "text-destructive" : "text-foreground"}>
                {line}
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Uses pure-WASM encoders (workerd-safe). Batches are small on purpose — each image
        does CPU-heavy decoding + AVIF encoding. Safe to interrupt: unfinished items stay
        pending, and re-running skips anything already done. Frontend components will start
        serving the AVIF/WebP variants automatically once wired.
      </p>
    </div>
  );
}

function StatCard({ label, value, tone }: { label: string; value: string; tone?: "success" | "warn" }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div
        className={
          "mt-1 text-2xl font-bold " +
          (tone === "success" ? "text-emerald-600" : tone === "warn" ? "text-amber-600" : "text-foreground")
        }
      >
        {value}
      </div>
    </div>
  );
}
